import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormLabel from "@mui/material/FormLabel";
import Checkbox from "@mui/material/Checkbox";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";

const typesChants = ["arabe", "français", "turc"];

const defaultForm = {
  nom: "",
  type_chants: [],
  contact_referent: "",
  ville_origine: "",
  materiel_fourni: false,
};

function Groupes() {
  const [groupes, setGroupes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchGroupes = async () => {
    try {
      const res = await api.get("/groupes");
      setGroupes(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchGroupes();
  }, []);

  const handleOpen = (groupe = null) => {
    if (groupe) {
      setForm({
        nom: groupe.nom,
        type_chants: Array.isArray(groupe.type_chants) ? groupe.type_chants : [],
        contact_referent: groupe.contact_referent,
        ville_origine: groupe.ville_origine,
        materiel_fourni: groupe.materiel_fourni,
      });
      setEditId(groupe.id);
    } else {
      setForm(defaultForm);
      setEditId(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm(defaultForm);
    setEditId(null);
  };

  const handleTypeChange = (type, checked) => {
    const newTypes = checked
      ? [...form.type_chants, type]
      : form.type_chants.filter((x) => x !== type);
    setForm({ ...form, type_chants: newTypes });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (editId) {
        await api.put(`/groupes/${editId}`, form);
      } else {
        await api.post("/groupes", form);
      }
      fetchGroupes();
      handleClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Supprimer ce groupe ?")) {
      await api.delete(`/groupes/${id}`);
      fetchGroupes();
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Groupes de chants
                </MDTypography>
                <MDButton
                  variant="outlined"
                  color="white"
                  size="small"
                  onClick={() => handleOpen()}
                >
                  <Icon>add</Icon>&nbsp;Ajouter
                </MDButton>
              </MDBox>
              <MDBox pt={3} px={3} pb={3}>
                <Grid container spacing={3}>
                  {groupes.map((groupe) => (
                    <Grid item xs={12} md={6} lg={4} key={groupe.id}>
                      <Card sx={{ p: 2, border: "1px solid #eee" }}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center">
                          <MDTypography variant="h6">{groupe.nom}</MDTypography>
                          <MDBox>
                            <Icon
                              sx={{ cursor: "pointer", mr: 1, color: "info.main" }}
                              onClick={() => handleOpen(groupe)}
                            >
                              edit
                            </Icon>
                            <Icon
                              sx={{ cursor: "pointer", color: "error.main" }}
                              onClick={() => handleDelete(groupe.id)}
                            >
                              cancel
                            </Icon>
                          </MDBox>
                        </MDBox>
                        <MDTypography variant="caption" color="text" display="block">
                          Types : {Array.isArray(groupe.type_chants)
                            ? groupe.type_chants.join(", ")
                            : groupe.type_chants}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block">
                          Ville : {groupe.ville_origine}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block">
                          Contact : {groupe.contact_referent}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block">
                          Matériel : {groupe.materiel_fourni ? "Oui" : "Non"}
                        </MDTypography>
                      </Card>
                    </Grid>
                  ))}
                  {groupes.length === 0 && (
                    <Grid item xs={12}>
                      <MDTypography variant="body2" color="text" textAlign="center">
                        Aucun groupe pour le moment.
                      </MDTypography>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? "Modifier le groupe" : "Ajouter un groupe"}</DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Nom du groupe"
              fullWidth
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
            />
            <MDBox>
              <FormLabel component="legend">Types de chants</FormLabel>
              <FormGroup row>
                {typesChants.map((t) => (
                  <FormControlLabel
                    key={t}
                    control={
                      <Checkbox
                        checked={form.type_chants.includes(t)}
                        onChange={(e) => handleTypeChange(t, e.target.checked)}
                      />
                    }
                    label={t}
                  />
                ))}
              </FormGroup>
            </MDBox>
            <TextField
              label="Contact référent"
              fullWidth
              value={form.contact_referent}
              onChange={(e) => setForm({ ...form, contact_referent: e.target.value })}
            />
            <TextField
              label="Ville d'origine"
              fullWidth
              value={form.ville_origine}
              onChange={(e) => setForm({ ...form, ville_origine: e.target.value })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.materiel_fourni}
                  onChange={(e) => setForm({ ...form, materiel_fourni: e.target.checked })}
                />
              }
              label="Matériel fourni par le groupe"
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose} color="secondary">
            Annuler
          </MDButton>
          <MDButton onClick={handleSubmit} color="info" disabled={loading}>
            {loading ? "Enregistrement..." : "Enregistrer"}
          </MDButton>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default Groupes;